import { Flex, Spacer } from "@hackthenorth/north";
import { Button, Label, Link } from "north.js";
import React, { useState } from "react";
import styled from "styled-components";

import { Dropzone, FileLink } from "src/shared/components";
import { JobBoardPreview1, JobBoardPreview2 } from "src/static/img";
import { JobPostingsData } from "src/views/sponsor/perks/sync";
import { PerkStatus, SponsorPerkType } from "src/views/sponsor/perks/types";

import {
  PerkContainer,
  PerkDatePicker,
  PerkError,
  PerkTextArea,
  PerkTextInput,
} from "../components";
import { PerkConfirmationModal } from "../components/PerkConfirmationModal";
import Header from "../Header";
import { getPerkStatusLabel, perkStatusToBadgeColor } from "../helpers";
import { TextDescription, TextInputTitle } from "../TextComponents";
import { useFileDropzone } from "../useFileDropzone";
import { PerkData } from "../usePerkState";

export const JOB_POSTINGS_VALIDATION_FIELDS: (keyof JobPostingsData)[] = [
  "job_postings_logo_file",
  "job_1_title",
  "job_1_desc",
  "job_1_link",
  "open_at_1",
];

export const JobPostings: React.FC<PerkData> = ({
  getState,
  updateState,
  isReadOnly,
  error,
}) => {
  const [showUnsubmitConfirm, setShowUnsubmitConfirm] = useState(false);

  const {
    dropzoneInputRef,
    fetchedFile,
    error: fileError,
    addFiles,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  } = useFileDropzone(getState("job_postings_logo_file") ?? [], (files) =>
    updateState("job_postings_logo_file", files)
  );

  return (
    <>
      <Header
        title="Job Postings"
        subtitle="Share up to three roles for our job board at hackthenorth.com."
        badge={
          <Label
            color={perkStatusToBadgeColor(
              getState("job_postings_status") as PerkStatus | undefined
            )}
            size="sm"
          >
            {getPerkStatusLabel(
              getState("job_postings_status") as PerkStatus | undefined
            )}
          </Label>
        }
      />
      <PerkContainer
        description={<Description />}
        id={SponsorPerkType.JOB_POSTINGS}
        readOnly={isReadOnly}
      >
        <TextDescription>
          Submit your company logo in SVG or PNG form (SVG is preferred). If you
          are submitting a PNG, please ensure the background is transparent and
          the image has a minimum height of 400px, so we can showcase your logo
          clearly!
        </TextDescription>
        <Spacer height={16} />
        {!isReadOnly && (
          <Dropzone
            max={1}
            acceptedFileTypes={["png", "svg"]}
            onFilesUpload={addFiles}
            onError={onFileError}
            ref={dropzoneInputRef}
            onBlur={() => setDropzoneErrorMessage("")}
          />
        )}
        {fetchedFile && (
          <>
            <Spacer height={32} />
            <FileLink file={fetchedFile} deleteFile={deleteFile} />
          </>
        )}
        {error?.job_postings_logo_file && (
          <PerkError error="This field is required" />
        )}
        {fileError && <PerkError error={fileError} />}
        <Spacer height={40} />
        <ListingFields
          listingNumber={1}
          isReadOnly={isReadOnly}
          error={error}
          getState={getState}
          updateState={updateState}
        />
        <Spacer height={40} />
        <ListingFields
          listingNumber={2}
          isReadOnly={isReadOnly}
          error={error}
          getState={getState}
          updateState={updateState}
        />
        <Spacer height={40} />
        <ListingFields
          listingNumber={3}
          isReadOnly={isReadOnly}
          error={error}
          getState={getState}
          updateState={updateState}
        />
      </PerkContainer>
      {getState("job_postings_status") === PerkStatus.APPROVED && (
        <>
          <Spacer height={32} />
          <Flex justify="flex-end" tw="w-full">
            <Button
              color="secondary"
              onClick={() => setShowUnsubmitConfirm(true)}
              size="md"
            >
              Unsubmit
            </Button>
          </Flex>
        </>
      )}
      <PerkConfirmationModal
        isOpen={showUnsubmitConfirm}
        title="Unsubmit job postings?"
        message="Are you sure you want to unsubmit? Your listings will move back to
            Incomplete and your listings may disappear from hackthenorth.com until 
            you submit again."
        confirmLabel="Yes"
        onClose={() => setShowUnsubmitConfirm(false)}
        onConfirm={() =>
          updateState("job_postings_status", PerkStatus.INCOMPLETE)
        }
      />
    </>
  );
};

const ListingFields: React.FC<{
  listingNumber: number;
  isReadOnly: boolean;
  error: PerkData["error"];
  getState: PerkData["getState"];
  updateState: PerkData["updateState"];
}> = ({ listingNumber: num, isReadOnly, error, getState, updateState }) => {
  const titleKey = `job_${num}_title` as keyof JobPostingsData;
  const descKey = `job_${num}_desc` as keyof JobPostingsData;
  const linkKey = `job_${num}_link` as keyof JobPostingsData;
  const openKey = `open_at_${num}` as keyof JobPostingsData;
  const closeKey = `close_at_${num}` as keyof JobPostingsData;

  const required = num === 1;

  return (
    <>
      <TextInputTitle>{`Job listing ${num}`}</TextInputTitle>
      <Spacer height={8} />
      <TextDescription>
        {required
          ? "All fields in this section are required to submit this perk."
          : "Optional"}
      </TextDescription>
      <Spacer height={16} />
      <PerkTextInput
        title={required ? "Job title*" : "Job title"}
        value={(getState(titleKey) as string) ?? ""}
        placeholder="e.g. Software Engineering Intern"
        isReadOnly={isReadOnly}
        onChange={(e) => updateState(titleKey, e.target.value)}
        error={error?.[titleKey] && <>This field is required</>}
      />
      <Spacer height={32} />
      <PerkTextArea
        title={required ? "Description*" : "Description"}
        value={(getState(descKey) as string) ?? ""}
        wordLimit={200}
        placeholder=""
        isReadOnly={isReadOnly}
        onChange={(e) => updateState(descKey, e.target.value)}
        error={error?.[descKey] && <>This field is required</>}
      />
      <Spacer height={32} />
      <PerkTextInput
        title={required ? "Application link*" : "Application link"}
        value={(getState(linkKey) as string) ?? ""}
        placeholder="https://"
        isReadOnly={isReadOnly}
        onChange={(e) => updateState(linkKey, e.target.value)}
        error={error?.[linkKey] && <>This field is required</>}
      />
      <Spacer height={32} />
      <PerkDatePicker
        title={required ? "Opens at*" : "Opens at"}
        value={(getState(openKey) as string) ?? ""}
        onChange={(date) => {
          if (date) updateState(openKey, date);
        }}
        isReadOnly={isReadOnly}
        error={error?.[openKey] && <>This field is required</>}
      />
      <Spacer height={32} />
      <PerkDatePicker
        title="Closes at"
        value={(getState(closeKey) as string) ?? ""}
        onChange={(date) => {
          if (date) updateState(closeKey, date);
        }}
        isReadOnly={isReadOnly}
        error={error?.[closeKey] && <>This field is required</>}
      />
    </>
  );
};

const Description = () => (
  <>
    <TextDescription>
      Upload a logo and details for each role you would like listed. You may
      submit up to 3. Each role will be published on{" "}
      <Link target="_blank" href="https://hackthenorth.com" rel="noreferrer">
        hackthenorth.com
      </Link>
      .
    </TextDescription>
    <Spacer height={16} />
    <PreviewImageContainer>
      <PreviewImage src={JobBoardPreview1} alt="Job board preview" />
      <PreviewImage src={JobBoardPreview2} alt="Job board preview" />
    </PreviewImageContainer>
  </>
);

const PreviewImageContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const PreviewImage = styled.img`
  flex: 1;
  min-width: 0;
  width: 100%;
  height: auto;
`;
